/**
 * CampusVideoTour — 90-second YouTube-embed panel that autoplays a
 * university's official campus walkthrough on the Degree Detail page.
 *
 * The component is INTENTIONALLY silent when `videoUrl` is falsy — so
 * degrees whose parent university hasn't been curated yet render
 * nothing rather than a "video coming soon" placeholder. This keeps
 * the page honest per the Radical-Honesty policy.
 *
 * Supports:
 *   • YouTube (any `watch?v=…` / `youtu.be/…` / `/embed/…` URL)
 *   • Vimeo (`vimeo.com/…`)
 *   • Raw .mp4 file URL (rendered via <video> element)
 *
 * Autoplay is muted (Chrome/Safari policy) and `playsinline` for iOS.
 * A single "Un-mute" button flips the audio state — because the whole
 * point is a 90-second get-a-feel-for-the-place, not a lecture.
 */
import { useMemo, useState } from "react";
import { Play, Volume2, VolumeX, MapPin } from "lucide-react";

/**
 * Return the shape of media to render. Returns null if we can't parse
 * the URL — the caller shows nothing in that case.
 */
function _parseVideoUrl(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  // YouTube — 3 common URL shapes.
  const yt = trimmed.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/
  );
  if (yt) {
    return { kind: "youtube", id: yt[1] };
  }
  const vimeo = trimmed.match(/vimeo\.com\/(\d+)/);
  if (vimeo) {
    return { kind: "vimeo", id: vimeo[1] };
  }
  if (/\.mp4($|\?)/i.test(trimmed)) {
    return { kind: "mp4", url: trimmed };
  }
  return null;
}


export default function CampusVideoTour({ videoUrl, uniName, city }) {
  const [muted, setMuted] = useState(true);
  const media = useMemo(() => _parseVideoUrl(videoUrl), [videoUrl]);
  if (!media) return null;

  const embedSrc = (() => {
    if (media.kind === "youtube") {
      const params = new URLSearchParams({
        autoplay: "1",
        mute: muted ? "1" : "0",
        modestbranding: "1",
        rel: "0",
        playsinline: "1",
      });
      return `https://www.youtube-nocookie.com/embed/${media.id}?${params.toString()}`;
    }
    if (media.kind === "vimeo") {
      const params = new URLSearchParams({
        autoplay: "1",
        muted: muted ? "1" : "0",
        title: "0",
        byline: "0",
        portrait: "0",
      });
      return `https://player.vimeo.com/video/${media.id}?${params.toString()}`;
    }
    return null;
  })();

  return (
    <section
      data-testid="campus-video-tour"
      className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5"
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-underground-sc tracking-wider text-slate-500 flex items-center gap-1.5">
            <Play className="h-3 w-3" />
            90-second campus tour
          </div>
          <h3 className="mt-1 text-base font-editorial tracking-tight text-slate-900">
            {uniName ? `Take a look around ${uniName}` : "Take a look around"}
          </h3>
          {city ? (
            <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-500">
              <MapPin className="h-3 w-3" />
              {city}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setMuted((m) => !m)}
          className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[10px] font-underground-sc tracking-wider text-slate-700 hover:bg-slate-50"
          data-testid="campus-video-mute-toggle"
        >
          {muted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
          {muted ? "Un-mute" : "Mute"}
        </button>
      </header>
      <div
        className="relative w-full overflow-hidden rounded bg-black"
        style={{ aspectRatio: "16 / 9" }}
      >
        {media.kind === "mp4" ? (
          <video
            key={muted ? "muted" : "loud"}
            src={media.url}
            autoPlay
            muted={muted}
            playsInline
            loop
            controls
            className="absolute inset-0 h-full w-full"
            data-testid="campus-video-mp4"
          />
        ) : (
          <iframe
            key={muted ? "muted" : "loud"}
            src={embedSrc}
            title={`Campus tour — ${uniName || "university"}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
            data-testid="campus-video-iframe"
          />
        )}
      </div>
    </section>
  );
}
